import axiosInstance from '../axios';
import { AxiosError } from 'axios';

// Interface for user profile data
export interface ProfileData {
  id: string;
  name: string;
  email: string;
  profile_image?: string;
  is_email_verified: boolean;
  is_active: boolean;
  created_at: string;
  date_of_birth?: string;
  organization_name?: string;
}

// Interface for notification settings
export interface NotificationSettings {
  card_sent: boolean;
  card_opened: boolean;
  /** Email 7 days and 1 day before reminders / contact birthdays & anniversaries */
  occasion_reminders?: boolean;
  special_promotions: boolean;
  new_features: boolean;
  system_updates: boolean;
}

// Interface for default card settings data
export interface DefaultCardSettingsData {
  appearance?: {
    envelopeColor: string;
    fontFamily: string;
    liningImage: string;
    textColor: string;
  };
  stamp?: string;
  confetti_settings?: {
    shapes: {
      circle: boolean;
      square: boolean;
      star: boolean;
      heart: boolean;
      emoji: boolean;
    };
    colors: string[];
    style: string;
  } | null;
  custom_logo?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Interface for reminder data
export interface Reminder {
  id: string;
  recipient_name: string;
  occasion: string;
  date: string;
  is_annual_reminder: boolean;
}

// Interface for user dashboard data
export interface UserDashboardData {
  total_cards_created: number;
  sent_cards: number;
  draft_cards: number;
  scheduled_cards: number;
  un_scheduled_cards: number;
}

// Interface for contact data
export interface Contact {
  id: string;
  full_name: string;
  email: string;
  date_of_birth : string;
  date_of_anniversary : string;
  date_of_work_anniversary : string;
  group: string;
  created_at: string;
  updated_at: string;
}

// Interface for API responses
export interface ApiResponse<T> {
  response_status: boolean;
  response_message: string;
  response_data: T | null;
}

// Helper function to handle axios errors
const handleAxiosError = (error: AxiosError): ApiResponse<any> => {
  if (error.response?.data) {
    const errorData = error.response.data as any;
    return {
      response_status: false,
      response_message: errorData.response_message || 'An error occurred',
      response_data: null
    };
  }
  return {
    response_status: false,
    response_message: error.message || 'Unknown error',
    response_data: null
  };
};

/**
 * Service for user profile and dashboard data
 */
export const UserService = {
  /**
   * Get user profile data
   * @returns Promise with the user profile data
   */
  getProfile: async (): Promise<ApiResponse<ProfileData>> => {
    try {
      const response = await axiosInstance.get('/profile/profile/');
      return response.data;
    } catch (error) {
      console.error('Error fetching profile data:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Get user dashboard data
   * @returns Promise with the user dashboard data
   */
  getUserDashboard: async (): Promise<ApiResponse<UserDashboardData>> => {
    try {
      const response = await axiosInstance.get('/profile/user-dashboard/');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Get user card list
   * @returns Promise with the user card list
   */
  getCardList: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.get('/profile/group-cards/');
      return response.data;
    } catch (error) {
      console.error('Error fetching card list:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Update user profile
   * @param payload - Profile update payload
   * @returns Promise with the update response
   */
  updateProfile: async (payload: Partial<ProfileData>): Promise<ApiResponse<ProfileData>> => {
    try {
      const response = await axiosInstance.put('/profile/profile/update/', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Fetch profile data - initializes the profile data fetch
   * This is a wrapper around getProfile for consistency with the Angular service
   * @returns Promise with the user profile data
   */
  fetchProfile: async (): Promise<ApiResponse<ProfileData>> => {
    return UserService.getProfile();
  },

  /**
   * Get notification settings
   * @returns Promise with the notification settings
   */
  getNotificationSettings: async (): Promise<ApiResponse<NotificationSettings>> => {
    try {
      const response = await axiosInstance.get('/profile/notification-settings/');
      return response.data;
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Update notification settings
   * @param payload - Notification settings payload
   * @returns Promise with the update response
   */
  updateNotificationSettings: async (payload: NotificationSettings): Promise<ApiResponse<NotificationSettings>> => {
    try {
      const response = await axiosInstance.put('/profile/notification-settings/update/', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Get user default card settings
   * @returns Promise with default card settings
   */
  getDefaultCardSettings: async (): Promise<ApiResponse<DefaultCardSettingsData>> => {
    try {
      const response = await axiosInstance.get('/profile/default-settings/');
      return response.data;
    } catch (error) {
      console.error('Error fetching default card settings:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Update user default card settings
   * @param payload - Default card settings payload
   * @returns Promise with updated default card settings
   */
  updateDefaultCardSettings: async (payload: Partial<DefaultCardSettingsData>): Promise<ApiResponse<DefaultCardSettingsData>> => {
    try {
      const response = await axiosInstance.put('/profile/default-settings/update/', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating default card settings:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Delete a card
   * @param cardId - ID of the card to delete
   * @returns Promise with the delete response
   */
  deleteCard: async (cardId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.delete(`/group-cards/${cardId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting card:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Send a card immediately
   * @param cardId - ID of the card to send
   * @returns Promise with the send response
   */
  sendCard: async (cardId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/group-cards/${cardId}/complete/`);
      return response.data;
    } catch (error) {
      console.error('Error sending card:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Clone a card
   * @param cardId - ID of the card to clone
   * @param overrides - Fields to change on the copy (title, recipient, schedule…)
   * @returns Promise with the clone response
   */
  cloneCard: async (cardId: string, overrides: Record<string, unknown> = {}): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/group-cards/${cardId}/clone/`, overrides);
      return response.data;
    } catch (error) {
      console.error('Error cloning card:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Get all reminders
   * @returns Promise with the reminders list
   */
  getReminders: async (): Promise<ApiResponse<Reminder[]>> => {
    try {
      const response = await axiosInstance.get('/profile/reminders/');
      return response.data;
    } catch (error) {
      console.error('Error fetching reminders:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Add a new reminder
   * @param reminder - Reminder data to add
   * @returns Promise with the added reminder
   */
  addReminder: async (reminder: Omit<Reminder, 'id'>): Promise<ApiResponse<Reminder>> => {
    try {
      const response = await axiosInstance.post('/profile/reminders/create/', reminder);
      return response.data;
    } catch (error) {
      console.error('Error adding reminder:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Update an existing reminder
   * @param id - ID of the reminder to update
   * @param reminder - Updated reminder data
   * @returns Promise with the updated reminder
   */
  updateReminder: async (id: string, reminder: Omit<Reminder, 'id'>): Promise<ApiResponse<Reminder>> => {
    try {
      const response = await axiosInstance.put(`/profile/reminders/${id}/update/`, reminder);
      return response.data;
    } catch (error) {
      console.error('Error updating reminder:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  /**
   * Delete a reminder
   * @param id - ID of the reminder to delete
   * @returns Promise with the delete response
   */
  deleteReminder: async (id: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.delete(`/profile/reminders/${id}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting reminder:', error);
      return handleAxiosError(error as AxiosError);
    }
  },
  // Create a new contact group
  createGroup: async (data: {group_name: string, description : string}): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post('/profile/contact-groups/create/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating contact:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //get all contact groups
  getAllGroups: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.get('/profile/contact-groups/');
      return response.data;
    } catch (error) {
      console.error('Error fetching contact groups:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //update contact group
  updateGroup: async (groupId: string, data: {group_name: string, description : string}): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.put(`/profile/contact-groups/${groupId}/update/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating contact group:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //delete contact group
  deleteGroup: async (groupId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.delete(`/profile/contact-groups/${groupId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting contact group:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //create a new contact
  createContact: async (data: any): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post('/profile/contacts/create/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating contact:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //get all contacts
  getAllContacts: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.get('/profile/contacts/');
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //update contact
  updateContact: async (contactId: string, data: any): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.put(`/profile/contacts/${contactId}/update/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating contact:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //delete contact
  deleteContact: async (contactId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.delete(`/profile/contacts/${contactId}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting contact:', error);
      return handleAxiosError(error as AxiosError);
    }
  },  

  //get contact by group id
  getContactsByGroupId: async (groupId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.get(`/profile/contacts/group/${groupId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts by group id:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //add contact to group
  addContactToGroup: async (data: { contact_id : string, group_id: string}): Promise<ApiResponse<any>> =>{
    try {
      const response = await axiosInstance.post('/profile/contact-groups/add-contact/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating contact:', error);
      return handleAxiosError(error as AxiosError);
    }
  },

  //remove contact from group
  removeContactToGroup: async (data: { contact_id: string, group_id: string }): Promise<ApiResponse<any>> => {
    try {
      const response = await axiosInstance.post('/profile/contact-groups/remove-contact/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating contact:', error);
      return handleAxiosError(error as AxiosError);
    }
  }
};
